/**
 * Custom react-flow edge that renders ELK's pre-computed
 * orthogonal route verbatim.
 *
 * Why this exists
 * ---------------
 * React-flow's built-in `smoothstep` / `step` edge types compute
 * the path purely from `sourceX/Y` and `targetX/Y` (the node
 * Handle positions). They're unaware of:
 *   • other nodes between source and target,
 *   • how many other edges share the same source/target,
 *   • where the actual layered layout wants the bend to be.
 *
 * ELK already solves all three when it lays out the graph with
 * `edgeRouting: ORTHOGONAL`, emitting `sections[]` with the exact
 * bend points. This component just paints what ELK decided —
 * which is why the noodle look goes away once it's in place.
 *
 * Path style
 * ----------
 * Polyline with a small (6 px) rounded corner at each bend.
 * Sharp 90° corners read too sterile against the soft block
 * styling; rounded reads more "wire diagram" without sliding
 * back into bezier-noodle territory.
 *
 * Used by the Power Tool canvas and the schematic / blocks
 * canvases. They all consume `ElkLayoutedEdge.sections` from
 * `useElkLayout`, so a single component handles every ELK-laid
 * graph in the app.
 */
import { BaseEdge, type EdgeProps } from "@xyflow/react";

import type { ElkEdgeSection, ElkPoint } from "./useElkLayout";

/** Shape required on `edge.data` for the orthogonal renderer.
 *  Pass `sections` straight from `ElkLayoutedEdge.sections` — the
 *  geometry ELK emitted on output. */
export interface OrthogonalEdgeData {
  sections?: ElkEdgeSection[];
  [key: string]: unknown;
}

/** Corner radius used at every bend. Small enough that the edge
 *  still reads as orthogonal at a glance, large enough that the
 *  corner doesn't pixel-shimmer. */
const CORNER_RADIUS = 6;

export function OrthogonalEdge(props: EdgeProps) {
  const data = props.data as OrthogonalEdgeData | undefined;
  const section = data?.sections?.[0];

  // Fallback: if ELK didn't produce sections (very small graphs,
  // or a layout where source==target), draw a straight line
  // between react-flow's source/target endpoints. Keeps the
  // edge visible rather than vanishing on edge cases.
  if (!section) {
    const path =
      `M ${props.sourceX},${props.sourceY} ` +
      `L ${props.targetX},${props.targetY}`;
    return (
      <BaseEdge
        id={props.id}
        path={path}
        style={props.style}
        markerEnd={props.markerEnd}
      />
    );
  }

  const points: ElkPoint[] = [
    section.startPoint,
    ...(section.bendPoints ?? []),
    section.endPoint,
  ];

  const path = buildRoundedOrthogonalPath(points, CORNER_RADIUS);

  return (
    <BaseEdge
      id={props.id}
      path={path}
      style={props.style}
      markerEnd={props.markerEnd}
    />
  );
}

/** Build an SVG `d` string from an orthogonal polyline with
 *  rounded corners at each interior bend.
 *
 *  At each interior point, instead of drawing a sharp 90° turn,
 *  we stop `r` units short of the bend along the incoming axis,
 *  draw a quadratic bezier whose control point IS the bend, and
 *  end `r` units along the outgoing axis. The bezier approximates
 *  a quarter-arc closely enough at small radii (the eye can't
 *  tell vs. an actual arc).
 *
 *  Edge cases:
 *    • Adjacent points closer than 2r → no rounding, draw straight.
 *    • Two points only (no bends)     → straight line. */
function buildRoundedOrthogonalPath(
  points: ElkPoint[],
  radius: number,
): string {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
  }

  const parts: string[] = [];
  parts.push(`M ${points[0].x},${points[0].y}`);

  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];

    // Allowed corner radius is the smaller of:
    //   • requested radius
    //   • half the distance to either neighbour (so the corner
    //     doesn't overshoot when segments are short)
    const inDist = distance(prev, curr);
    const outDist = distance(curr, next);
    const r = Math.min(radius, inDist / 2, outDist / 2);

    if (r <= 0.5) {
      // Skip rounding when there's no room — straight to the bend.
      parts.push(`L ${curr.x},${curr.y}`);
      continue;
    }

    const enter = pointAlong(curr, prev, r);
    const exit = pointAlong(curr, next, r);
    parts.push(`L ${enter.x},${enter.y}`);
    parts.push(`Q ${curr.x},${curr.y} ${exit.x},${exit.y}`);
  }

  const last = points[points.length - 1];
  parts.push(`L ${last.x},${last.y}`);
  return parts.join(" ");
}

function distance(a: ElkPoint, b: ElkPoint): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Return the point `r` units away from `from` in the direction
 *  of `toward`. Used to compute the entry/exit of a rounded
 *  corner. */
function pointAlong(from: ElkPoint, toward: ElkPoint, r: number): ElkPoint {
  const dx = toward.x - from.x;
  const dy = toward.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return {
    x: from.x + (dx / len) * r,
    y: from.y + (dy / len) * r,
  };
}
